import sha1 from 'js-sha1';
import { bytesToNDigits } from 'lib/bytes';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toolbarActions } from 'reactApp/appHelpers/toolbarActions';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { useActionsConfig } from 'reactApp/hooks/useActionsConfig';
import { getWeblinkFromPublicId } from 'reactApp/modules/file/utils';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { resetSelect, selectOne } from 'reactApp/modules/selections/selections.actions';
import { changeStorageHistory, getStorage } from 'reactApp/modules/storage/storage.helpers';
import { getStorageItemById } from 'reactApp/modules/storage/storage.selectors';
import { EActions, EStorageType } from 'reactApp/modules/storage/storage.types';
import { RootState } from 'reactApp/store';
import { formatFileInfoDate } from 'reactApp/utils/formatDate';
import { sendPaymentGa } from 'reactApp/utils/paymentGa';

import { fileInfoItems } from './useItems.data';
import { closeFileInfoModal, closeViewerModal, getFileDirection, getFileName, getShareAccess } from './useItems.helpers';
import { UseItemsHook } from './useItems.types';

const { filename, uploadDate, size: sizeInfo, direction: directionInfo, share, removeDate, originalPlace } = fileInfoItems;

export const useItems: UseItemsHook = ({ id }) => {
    const [fullName, setFullName] = useState(false);
    const dispatch = useDispatch();
    const storage = useSelector(getCurrentStorage);
    const availableActions = useActionsConfig({ id, isMobile: true });
    const isPublishEnabled = availableActions[EActions.publish];
    const { isTrashBin, isPublic, isGallery } = getStorage(storage);
    const { ext, mtime, nameWithoutExt, name, weblink, size, deletedAt, deletedFrom, home, isFolder } = useSelector((state: RootState) =>
        getStorageItemById(state, storage, id)
    ) as any;

    const path = isTrashBin ? deletedFrom : home;
    const direction = path ? path.split('/')?.filter(Boolean) : undefined;
    const directionWithoutLast = direction?.slice(0, direction.length - 1);

    const isRootDir = path === ROOT_FOLDER_ID;

    const hasUploadDate = !isTrashBin && !isFolder && !!mtime;
    const hasShare = !isRootDir && !!isPublishEnabled;
    const hasDirection = !isTrashBin && !isRootDir && direction;

    const onSelect = useCallback(() => {
        const selectedIdx = isTrashBin ? path : `/${directionWithoutLast?.join('/')}`;
        changeStorageHistory({
            id: selectedIdx,
            ...(isPublic || isGallery || isTrashBin ? { navigateToStorage: EStorageType.home } : { storage }),
        });
        closeFileInfoModal();
        closeViewerModal();
        sendPaymentGa({
            eventCategory: 'viewer',
            action: 'view_in_folder',
            source: storage,
            id_media: sha1(id),
            id_public: isPublic ? getWeblinkFromPublicId(id) : 'None',
        });
    }, [directionWithoutLast, isGallery, isPublic, isTrashBin, path, storage]);

    const onShare = useCallback(() => {
        dispatch(selectOne({ selectedIdx: id, storage }));
        toolbarActions.publish('mobile-file-info');
        closeFileInfoModal();
        dispatch(resetSelect());
    }, [dispatch, id, storage]);

    const toggleFullName = useCallback(() => {
        setFullName((value) => !value);
    }, []);

    const fileInfoItemsRecord = {
        filename: {
            ...filename,
            rightIcon: fullName ? 'up' : 'down',
            value: getFileName(fullName ? name : nameWithoutExt || name, fullName ? '' : ext, fullName),
            onClick: toggleFullName,
        },
        uploadDate: hasUploadDate && { ...uploadDate, value: formatFileInfoDate(mtime) },
        removeDate: isTrashBin && { ...removeDate, value: formatFileInfoDate(deletedAt) },
        size: size && { ...sizeInfo, value: bytesToNDigits(size, 3).value },
        share: hasShare && { ...share, active: Boolean(weblink), value: getShareAccess(weblink), onClick: onShare },
        direction: hasDirection && { ...directionInfo, value: getFileDirection(directionWithoutLast), onClick: onSelect },
        originalPlace: isTrashBin && { ...originalPlace, value: getFileDirection(direction), onClick: onSelect },
    };

    return {
        header: `Информация о ${isFolder ? 'папке' : 'файле'}`,
        items: Object.values(fileInfoItemsRecord).filter(Boolean),
    };
};
